import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useStrings } from '../../i18n';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/adminService';
import MetricsCards from '../../components/MetricsCards';
import ReconciliationTable from '../../components/ReconciliationTable';
import RetryShipmentModal from '../../components/RetryShipmentModal';

// ─── Constants ─────────────────────────────────────────────────────────────────
const AUTO_REFRESH_INTERVAL_MS = 30_000; // 30 seconds
const STUCK_THRESHOLD_MS       = 10 * 60 * 1000; // 10 minutes
const PAGE_SIZE                = 40;

// ─── Derived helpers ────────────────────────────────────────────────────────────
function deriveIsStuck(order) {
  if (order.shiprocket?.status !== 'processing') return false;
  const started = order.shiprocket?.processing_started_at;
  if (!started) return false;
  return Date.now() - new Date(started).getTime() > STUCK_THRESHOLD_MS;
}

function deriveIsSlaBreached(order) {
  const deadline = order.shiprocket?.sla_deadline;
  if (!deadline) return false;
  if (order.shiprocket?.status === 'completed') return false;
  return new Date(deadline) < new Date();
}

function deriveNeedsAttention(order) {
  return (
    order.shiprocket?.status === 'permanent_failed' ||
    deriveIsStuck(order) ||
    deriveIsSlaBreached(order)
  );
}

// ─── Status filter → Shiprocket param mapping ────────────────────────────────
const FILTER_PARAM = {
  processing: 'processing',
  retrying:   'failed_reconciling',
  failed:     'permanent_failed',
  completed:  'completed',
};

// ─── Main page component ─────────────────────────────────────────────────────
export default function AdminReconciliationMonitor() {
  const strings = useStrings();
  const { getIdToken } = useAuth();
  const s = strings.adminMonitor || {};

  // ── Data state
  const [orders, setOrders]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing]   = useState(false);
  const [page, setPage]               = useState(1);
  const [totalPages, setTotalPages]   = useState(1);

  // ── Filter state
  const [statusFilter, setStatusFilter] = useState('all');
  const [timeFilter, setTimeFilter]     = useState('24h');

  // ── UI state
  const [autoRefresh, setAutoRefresh]   = useState(true);
  const [retryOrder, setRetryOrder]     = useState(null);

  const intervalRef = useRef(null);

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const fetchOrders = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);

      const idToken = await getIdToken();
      const params  = { page, limit: PAGE_SIZE };

      if (statusFilter !== 'all' && FILTER_PARAM[statusFilter]) {
        params.shipmentStatus = FILTER_PARAM[statusFilter];
      }
      if (timeFilter === '1h') params.since = new Date(Date.now() - 3600_000).toISOString();
      if (timeFilter === '24h') params.since = new Date(Date.now() - 86_400_000).toISOString();

      const data = await adminAPI.orders.list(params, idToken);
      setOrders(data.orders ?? []);
      setTotalPages(data.pagination?.totalPages ?? 1);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getIdToken, page, statusFilter, timeFilter]);

  // Initial load + when filters/page change
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Auto-refresh interval
  useEffect(() => {
    if (!autoRefresh) {
      clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => fetchOrders({ silent: true }), AUTO_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh, fetchOrders]);

  // ── Derived metrics ──────────────────────────────────────────────────────────
  const counts = useMemo(() => ({
    processing: orders.filter((o) => o.shiprocket?.status === 'processing').length,
    retrying:   orders.filter((o) => o.shiprocket?.status === 'failed_reconciling').length,
    failed:     orders.filter((o) => o.shiprocket?.status === 'permanent_failed').length,
    completed:  orders.filter((o) => o.shiprocket?.status === 'completed').length,
    stuck:      orders.filter((o) => deriveIsStuck(o)).length,
    slaBreach:  orders.filter((o) => deriveIsSlaBreached(o)).length,
  }), [orders]);

  // ── Critical alerts (needs attention) ────────────────────────────────────────
  const alertOrders = useMemo(
    () => orders.filter(deriveNeedsAttention).slice(0, 5),
    [orders],
  );

  // ── Front-end filter for "stuck" / "slaBreach" special cases ────────────────
  const filteredOrders = useMemo(() => {
    if (statusFilter === 'stuck')           return orders.filter(deriveIsStuck);
    if (statusFilter === 'slaBreach')       return orders.filter(deriveIsSlaBreached);
    if (statusFilter === 'needsAttention')  return orders.filter(deriveNeedsAttention);
    return orders;
  }, [orders, statusFilter]);

  // ── Status filter → page reset
  const handleStatusFilter = (key) => {
    setStatusFilter(key);
    setPage(1);
  };

  // ── Retry ────────────────────────────────────────────────────────────────────
  const handleRetryQueued = useCallback(() => {
    setRetryOrder(null);
    fetchOrders({ silent: true });
  }, [fetchOrders]);

  // ── Helpers
  const fmt = (d) => d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20 fade-section show">

      {/* Back Button */}
      <Link to="/admin" className="inline-flex items-center text-secondary hover:text-secondary/80 mb-6 transition-colors">
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        {strings.admin?.backToDashboard || 'Back to Dashboard'}
      </Link>

      {/* ── Page header ── */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading text-headingText">
            {s.title || 'Shipment Monitor'}
          </h1>
          <p className="text-bodyText font-sans mt-1 text-sm">
            {s.subtitle || 'Real-time operations dashboard — detect and fix shipment issues'}
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Auto-refresh toggle */}
          <button
            onClick={() => setAutoRefresh((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-sans font-medium border transition-colors ${
              autoRefresh
                ? 'bg-secondary/10 border-secondary text-secondary'
                : 'bg-surface border-secondary/30 text-textSecondary'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-secondary animate-pulse' : 'bg-textSecondary/40'}`} />
            {s.autoRefresh || 'Auto-refresh'}: {autoRefresh ? (s.autoRefreshOn || 'ON') : (s.autoRefreshOff || 'OFF')}
          </button>

          {/* Manual refresh */}
          <button
            onClick={() => fetchOrders({ silent: true })}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-sans font-medium bg-cardBg border border-secondary/20 hover:border-secondary/50 text-bodyText disabled:opacity-50 transition-colors"
          >
            <svg className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {refreshing ? (s.refreshing || 'Refreshing…') : (s.manualRefresh || 'Refresh now')}
          </button>

          {/* Last updated */}
          {lastUpdated && (
            <span className="text-xs font-sans text-textSecondary">
              {s.lastUpdated || 'Last updated'}: {fmt(lastUpdated)}
            </span>
          )}
        </div>
      </div>

      {/* ── Error banner ── */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-sans">
          {error}
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {loading && (
        <div className="space-y-4 mb-8">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-cardBg rounded-2xl shadow-card h-24 animate-pulse" />
            ))}
          </div>
          <div className="bg-cardBg rounded-2xl shadow-card h-64 animate-pulse" />
        </div>
      )}

      {!loading && (
        <>
          {/* ── Metrics cards ── */}
          <div className="mb-6">
            <MetricsCards
              counts={counts}
              activeFilter={statusFilter}
              onFilter={handleStatusFilter}
              strings={strings}
            />
          </div>

          {/* ── Critical alerts panel ── */}
          {alertOrders.length > 0 ? (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-2xl p-5">
              <h2 className="text-base font-heading text-red-800 mb-3 flex items-center gap-2">
                <span>🚨</span>
                {s.alertsTitle || 'Needs Attention'}
                <span className="ml-1 px-2 py-0.5 bg-red-200 text-red-800 rounded-full text-xs font-sans font-medium">
                  {alertOrders.length}
                </span>
              </h2>
              <div className="flex flex-wrap gap-2">
                {alertOrders.map((order) => {
                  const stuck   = deriveIsStuck(order);
                  const slaBr   = deriveIsSlaBreached(order);
                  const failed  = order.shiprocket?.status === 'permanent_failed';
                  return (
                    <div key={order._id} className="flex items-center gap-2 bg-white border border-red-200 rounded-xl px-3 py-2 text-xs font-sans">
                      <span className="font-medium text-headingText">{order.orderId}</span>
                      {failed && <span className="text-red-700">{s.alertFailed || 'Permanent Failure'}</span>}
                      {stuck  && <span className="text-orange-700">{s.alertStuck || 'Stuck'}</span>}
                      {slaBr  && <span className="text-orange-700">{s.alertSla || 'SLA Breached'}</span>}
                      {order.shiprocket?.status === 'permanent_failed' && (
                        <button
                          onClick={() => setRetryOrder(order)}
                          className="text-secondary font-medium hover:underline"
                        >
                          {s.actionRetry || 'Retry'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-2xl p-4 text-sm font-sans text-green-800 flex items-center gap-2">
              <span>✅</span>
              <span>{s.noAlerts || 'No critical issues — all shipments healthy'}</span>
            </div>
          )}

          {/* ── Filters bar ── */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="text-xs font-sans text-textSecondary mr-1">Filter:</span>

            {[
              { key: 'all', label: s.filterAll || 'All' },
              { key: 'processing', label: s.filterProcessing || 'Processing' },
              { key: 'retrying',   label: s.filterRetrying || 'Retrying' },
              { key: 'failed',     label: s.filterFailed || 'Failed' },
              { key: 'completed',  label: s.filterShipped || 'Shipped' },
              { key: 'stuck',      label: s.filterStuck || 'Stuck' },
              { key: 'slaBreach',  label: s.filterSlaBreach || 'SLA Breached' },
              { key: 'needsAttention', label: s.filterNeedsAttention || 'Needs Attention' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handleStatusFilter(key)}
                className={`px-3 py-1 rounded-xl text-xs font-sans transition-colors border ${
                  statusFilter === key
                    ? 'bg-secondary text-white border-secondary'
                    : 'bg-cardBg border-secondary/20 text-bodyText hover:border-secondary/50'
                }`}
              >
                {label}
              </button>
            ))}

            {/* Divider */}
            <span className="text-secondary/30">|</span>

            {/* Time filter */}
            {[
              { key: '1h',   label: s.timeFilter1h || 'Last 1h' },
              { key: '24h',  label: s.timeFilter24h || 'Last 24h' },
              { key: 'all',  label: s.timeFilterAll || 'All time' },
            ].map(({ key, label }) => (
              <button
                key={`time-${key}`}
                onClick={() => { setTimeFilter(key); setPage(1); }}
                className={`px-3 py-1 rounded-xl text-xs font-sans transition-colors border ${
                  timeFilter === key
                    ? 'bg-secondary/20 text-secondary border-secondary'
                    : 'bg-cardBg border-secondary/20 text-bodyText hover:border-secondary/50'
                }`}
              >
                {label}
              </button>
            ))}

            {/* Order count */}
            <span className="ml-auto text-xs font-sans text-textSecondary">
              {filteredOrders.length} {filteredOrders.length === 1 ? 'order' : 'orders'}
            </span>
          </div>

          {/* ── Table ── */}
          <ReconciliationTable
            orders={filteredOrders}
            onRetry={setRetryOrder}
            strings={s}
          />

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-xs font-sans rounded-xl border border-secondary/20 bg-cardBg text-bodyText hover:border-secondary/50 disabled:opacity-40 transition-colors"
              >
                ← Prev
              </button>
              <span className="text-xs font-sans text-textSecondary">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 text-xs font-sans rounded-xl border border-secondary/20 bg-cardBg text-bodyText hover:border-secondary/50 disabled:opacity-40 transition-colors"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* ── Retry Modal ── */}
      {retryOrder && (
        <RetryShipmentModal
          order={retryOrder}
          getIdToken={getIdToken}
          onClose={() => setRetryOrder(null)}
          onRetryQueued={handleRetryQueued}
          strings={s}
        />
      )}
    </div>
  );
}
